import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
  DEFAULT_PROPERTIES,
  EO_DEFAULT_PROPERTIES,
  mapColumnData,
} from '../../../../page/CertificationDetails/CertsExport/CertsExportsUtils';
import { certsListExportColumnsToExport } from '../../../../redux/certlist/action';
import {
  COL_FILTER_HEADER_ACCOUNT,
  COL_FILTER_HEADER_CHILD_ENTITLEMENT,
  COL_FILTER_HEADER_ENTITLEMENT,
  COL_FILTER_HEADER_ROLE,
  COL_FILTER_HEADER_USER,
  EO_OBJECTS_TO_BE_INCLUDED_CHILD_ENTITLEMENTS,
  OBJECTS_TO_BE_INCLUDED_ACCOUNTS,
  OBJECTS_TO_BE_INCLUDED_ENTITLEMENTS,
  OBJECTS_TO_BE_INCLUDED_ROLES,
  OBJECTS_TO_BE_INCLUDED_USERS,
} from '../../../ViewControls';
import { CAMP_TYPE_EO } from '../../CertsListTile/constant';

import SelectAndOrganizeYourData from './SelectAndOrganizeYourData';

function CertsListSelectAndOrganizeYourData({ certKey, campaignType }) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [selection, setSelection] = useState(1);
  const [allColumnsData, setAllColumnsData] = useState({});

  const { messages } = useSelector((state) => state?.user) || {};

  const isEOCampaign = campaignType === CAMP_TYPE_EO;

  const {
    lineAction: { columnPickerList, objectsToBeIncluded },
  } = useSelector((state) => state?.certifications?.list);

  const objectsToBeIncludedForCurrentCertKey =
    objectsToBeIncluded && objectsToBeIncluded[certKey.toString()];

  const showUsers = objectsToBeIncludedForCurrentCertKey?.includes(
    OBJECTS_TO_BE_INCLUDED_USERS
  );
  const showRoles = objectsToBeIncludedForCurrentCertKey?.includes(
    OBJECTS_TO_BE_INCLUDED_ROLES
  );
  const showAccounts = objectsToBeIncludedForCurrentCertKey?.includes(
    OBJECTS_TO_BE_INCLUDED_ACCOUNTS
  );
  const showEntitlements = objectsToBeIncludedForCurrentCertKey?.includes(
    OBJECTS_TO_BE_INCLUDED_ENTITLEMENTS
  );

  const showChildEntitlements = objectsToBeIncludedForCurrentCertKey?.includes(
    EO_OBJECTS_TO_BE_INCLUDED_CHILD_ENTITLEMENTS
  );

  const availableColumnObject = () => {
    const arr = [];

    if (isEOCampaign) {
      if (showEntitlements) arr.push(COL_FILTER_HEADER_ENTITLEMENT);
      if (showAccounts) arr.push(COL_FILTER_HEADER_ACCOUNT);
      if (showChildEntitlements) arr.push(COL_FILTER_HEADER_CHILD_ENTITLEMENT);
    } else {
      if (showUsers) arr.push(COL_FILTER_HEADER_USER);
      if (showAccounts) arr.push(COL_FILTER_HEADER_ACCOUNT);
      if (showEntitlements) arr.push(COL_FILTER_HEADER_ENTITLEMENT);
      if (showRoles) arr.push(COL_FILTER_HEADER_ROLE);
      if (showChildEntitlements) arr.push(COL_FILTER_HEADER_CHILD_ENTITLEMENT);
    }

    return arr;
  };

  const availableObjects = availableColumnObject();

  const selectedTab =
    availableObjects.length && availableObjects[selection - 1];

  useEffect(() => {
    if (!columnPickerList) return;

    const transformColumns = (columnList) =>
      Object.entries(columnList).reduce((acc, [key, columns]) => {
        const enhancedColumns = isEOCampaign
          ? { ...columns, ...EO_DEFAULT_PROPERTIES[key] }
          : { ...columns, ...DEFAULT_PROPERTIES[key] };

        acc[key] = mapColumnData(
          isEOCampaign,
          Object.values(enhancedColumns),
          messages,
          intl,
          key
        );

        return acc;
      }, {});

    const allColumnsWithLabels = transformColumns(columnPickerList);

    setAllColumnsData(allColumnsWithLabels);
  }, [columnPickerList, messages, certKey]);

  useEffect(() => {
    const columnsToExport = {};

    const addFilteredColumns = (key, shouldShow) => {
      if (shouldShow && allColumnsData?.[key]) {
        columnsToExport[key] = allColumnsData[key]
          .filter((column) => column.isChecked)
          .map((column) => column.id.split('.')[0]);
      }
    };

    if (isEOCampaign) {
      addFilteredColumns(COL_FILTER_HEADER_ENTITLEMENT, showEntitlements);
      addFilteredColumns(COL_FILTER_HEADER_ACCOUNT, showAccounts);
      addFilteredColumns(
        COL_FILTER_HEADER_CHILD_ENTITLEMENT,
        showChildEntitlements
      );
    } else {
      addFilteredColumns(COL_FILTER_HEADER_USER, showUsers);
      addFilteredColumns(COL_FILTER_HEADER_ACCOUNT, showAccounts);
      addFilteredColumns(COL_FILTER_HEADER_ENTITLEMENT, showEntitlements);
      addFilteredColumns(COL_FILTER_HEADER_ROLE, showRoles);
      addFilteredColumns(
        COL_FILTER_HEADER_CHILD_ENTITLEMENT,
        showChildEntitlements
      );
    }

    dispatch(certsListExportColumnsToExport(columnsToExport));
  }, [
    allColumnsData,
    certKey,
    showUsers,
    showRoles,
    showAccounts,
    showEntitlements,
    showChildEntitlements,
  ]);

  return (
    <SelectAndOrganizeYourData
      selection={selection}
      setSelection={setSelection}
      showUsers={showUsers}
      showRoles={showRoles}
      showAccounts={showAccounts}
      showEntitlements={showEntitlements}
      showChildEntitlements={showChildEntitlements}
      allColumnsData={allColumnsData}
      setAllColumnsData={setAllColumnsData}
      selectedTab={selectedTab}
      campaignType={campaignType}
    />
  );
}

CertsListSelectAndOrganizeYourData.propTypes = {
  certKey: PropTypes.number.isRequired,
  campaignType: PropTypes.number.isRequired,
};

export default CertsListSelectAndOrganizeYourData;
